import type {
  PagePoint,
  ShapeBounds,
  ShapeFill,
  ShapePreset,
  ShapeStroke,
  ShapeStyle,
} from "./types.js";

export const defaultStroke: ShapeStroke = {
  color: "#1f6feb",
  width: 2,
  opacity: 1,
  cap: "round",
  join: "round",
  startArrow: "none",
  endArrow: "none",
};

export const defaultFill: ShapeFill = {
  kind: "solid",
  color: "#dff0ff",
};

export const defaultShapeStyle: ShapeStyle = {
  fill: defaultFill,
  stroke: defaultStroke,
  opacity: 1,
  quickStyleId: "blue-soft",
};

const noFillStyle: ShapeStyle = {
  fill: { kind: "none" },
  stroke: defaultStroke,
  opacity: 1,
};

function point(x: number, y: number): PagePoint {
  return { space: "page", x, y };
}

function polygonFromBounds(bounds: ShapeBounds): readonly PagePoint[] {
  return [
    point(bounds.x + bounds.width / 2, bounds.y + bounds.height),
    point(bounds.x + bounds.width, bounds.y),
    point(bounds.x, bounds.y),
  ];
}

function diamondFromBounds(bounds: ShapeBounds): readonly PagePoint[] {
  return [
    point(bounds.x + bounds.width / 2, bounds.y + bounds.height),
    point(bounds.x + bounds.width, bounds.y + bounds.height / 2),
    point(bounds.x + bounds.width / 2, bounds.y),
    point(bounds.x, bounds.y + bounds.height / 2),
  ];
}

function starFromBounds(bounds: ShapeBounds): readonly PagePoint[] {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const outer = Math.min(bounds.width, bounds.height) / 2;
  const inner = outer * 0.45;
  return Array.from({ length: 10 }, (_, index) => {
    const angle = Math.PI / 2 + (index * Math.PI) / 5;
    const radius = index % 2 === 0 ? outer : inner;
    return point(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius);
  });
}

export const shapePresets: readonly ShapePreset[] = [
  {
    id: "rectangle",
    label: "Rectangle",
    group: "basic",
    defaultBounds: { x: 92, y: 556, width: 180, height: 86 },
    defaultStyle: defaultShapeStyle,
    powerPointTarget: { kind: "geometricShape", id: "Rectangle" },
  },
  {
    id: "ellipse",
    label: "Ellipse",
    group: "basic",
    defaultBounds: { x: 306, y: 558, width: 144, height: 84 },
    defaultStyle: {
      ...defaultShapeStyle,
      fill: { kind: "solid", color: "#f3e8ff" },
      stroke: { ...defaultStroke, color: "#7c3aed" },
      quickStyleId: "violet-soft",
    },
    powerPointTarget: { kind: "geometricShape", id: "Ellipse" },
  },
  {
    id: "line",
    label: "Line",
    group: "lines",
    defaultBounds: { x: 96, y: 502, width: 178, height: 44 },
    defaultStyle: noFillStyle,
    createPoints: (bounds) => [
      point(bounds.x, bounds.y),
      point(bounds.x + bounds.width, bounds.y + bounds.height),
    ],
    powerPointTarget: { kind: "lineTool", id: "line" },
  },
  {
    id: "arrow",
    label: "Arrow",
    group: "arrows",
    defaultBounds: { x: 306, y: 504, width: 168, height: 42 },
    defaultStyle: {
      ...noFillStyle,
      stroke: { ...defaultStroke, color: "#0f766e", endArrow: "triangle" },
      quickStyleId: "teal-arrow",
    },
    createPoints: (bounds) => [
      point(bounds.x, bounds.y),
      point(bounds.x + bounds.width, bounds.y + bounds.height),
    ],
    powerPointTarget: { kind: "lineTool", id: "lineArrow" },
  },
  {
    id: "polygon",
    label: "Triangle",
    group: "basic",
    defaultBounds: { x: 104, y: 402, width: 132, height: 92 },
    defaultStyle: {
      ...defaultShapeStyle,
      fill: { kind: "solid", color: "#fff2c2" },
      stroke: { ...defaultStroke, color: "#b45309" },
      quickStyleId: "amber-triangle",
    },
    createPoints: polygonFromBounds,
    powerPointTarget: { kind: "geometricShape", id: "Triangle" },
  },
  {
    id: "flowchartProcess",
    label: "Process",
    group: "flowchart",
    defaultBounds: { x: 104, y: 398, width: 150, height: 82 },
    defaultStyle: {
      ...defaultShapeStyle,
      fill: { kind: "solid", color: "#edf5ff" },
      stroke: { ...defaultStroke, color: "#2563eb" },
      quickStyleId: "blue-process",
    },
    powerPointTarget: { kind: "geometricShape", id: "FlowChartProcess" },
  },
  {
    id: "flowchartDecision",
    label: "Decision",
    group: "flowchart",
    defaultBounds: { x: 300, y: 398, width: 150, height: 96 },
    defaultStyle: {
      ...defaultShapeStyle,
      fill: { kind: "solid", color: "#e8fff6" },
      stroke: { ...defaultStroke, color: "#047857" },
      quickStyleId: "green-diamond",
    },
    createPoints: diamondFromBounds,
    powerPointTarget: { kind: "geometricShape", id: "FlowChartDecision" },
  },
  {
    id: "callout",
    label: "Callout",
    group: "callouts",
    defaultBounds: { x: 112, y: 288, width: 210, height: 76 },
    defaultStyle: {
      ...defaultShapeStyle,
      fill: { kind: "solid", color: "#fff4de" },
      stroke: { ...defaultStroke, color: "#d97706" },
      quickStyleId: "orange-callout",
    },
    createPoints: (bounds) => [
      point(bounds.x - 50, bounds.y + bounds.height * 0.32),
      point(bounds.x - 12, bounds.y + bounds.height * 0.52),
      point(bounds.x, bounds.y + bounds.height * 0.52),
    ],
    defaultText: {
      value: "確認",
      fontSize: 14,
      color: "#2f2416",
      align: "center",
    },
    powerPointTarget: { kind: "geometricShape", id: "WedgeRRectCallout" },
  },
  {
    id: "star5",
    label: "Star",
    group: "stars",
    defaultBounds: { x: 388, y: 290, width: 92, height: 92 },
    defaultStyle: {
      ...defaultShapeStyle,
      fill: { kind: "solid", color: "#ffe4ef" },
      stroke: { ...defaultStroke, color: "#be185d" },
      quickStyleId: "pink-star",
    },
    createPoints: starFromBounds,
    powerPointTarget: { kind: "geometricShape", id: "Star5" },
  },
  {
    id: "actionButton",
    label: "Action",
    group: "action",
    defaultBounds: { x: 352, y: 206, width: 132, height: 52 },
    defaultStyle: {
      ...defaultShapeStyle,
      fill: { kind: "solid", color: "#e6f7ed" },
      stroke: { ...defaultStroke, color: "#15803d" },
      quickStyleId: "green-action",
    },
    defaultText: {
      value: "Open",
      fontSize: 13,
      color: "#12351f",
      align: "center",
    },
    defaultAction: { kind: "url", href: "https://pdf.ticket.jp" },
    powerPointTarget: { kind: "geometricShape", id: "ActionButtonBlank" },
  },
];

export function getShapePreset(id: ShapePreset["id"]): ShapePreset {
  const preset = shapePresets.find((item) => item.id === id);
  if (!preset) {
    throw new Error(`unknown shape preset: ${id}`);
  }
  return preset;
}
